<script setup lang="ts">
import { computed } from 'vue'

import type { LazyLoad, Media } from '@fondof-ui/components/media/types'
import {
  MediaImage,
  MediaRoot,
} from '@storefront-satch/components/molecules/media'
import MediaVideo from '@storefront-satch/components/molecules/media/MediaVideo.vue'
import type {
  TeaserCardItemAdditionalProps,
  TeaserCardItemProps,
} from '@storefront-satch/components/molecules/teaser-card/types'
import { teaserCardItemMediaVariants } from '@storefront-satch/components/molecules/teaser-card/variants'

const props = withDefaults(
  defineProps<{
    media: Media
    type: TeaserCardItemProps['type']
    size: TeaserCardItemProps['size']
    width: TeaserCardItemProps['width']
    groupType: TeaserCardItemAdditionalProps['groupType']
    mediaAspectRatio?: string
    showPlayButton?: boolean
    showActive?: boolean
    /**
     * The object-fit property for the media element.
     */
    mediaFit?: 'cover' | 'contain'
  }>(),
  {
    mediaFit: 'cover',
  }
)

const emit = defineEmits<{
  'update:video-playing': [value: boolean]
}>()

const aspectClasses = computed(() => {
  return {
    '!aspect-square': props.mediaAspectRatio === '1/1',
    '!aspect-[2/1]': props.mediaAspectRatio === '2/1',
    '!aspect-[5/4]': props.mediaAspectRatio === '5/4',
  }
})

const mediaEntry = computed(() => ({
  lazyload: 'lazy' as LazyLoad,
  ...props.media,
}))
</script>

<template>
  <MediaRoot
    :entry="mediaEntry"
    :class="[
      aspectClasses,
      teaserCardItemMediaVariants({
        size: props.size,
        type: props.type,
        width: props.width,
        groupType: props.groupType,
        showActive: props.showActive,
      }),
    ]"
  >
    <template #video="{ data }">
      <MediaVideo
        v-bind="data"
        class="size-full"
        :show-play-button="false"
        @update:video-playing="
          (state) => {
            emit('update:video-playing', state)
          }
        "
      />
    </template>

    <template #image="{ data }">
      <MediaImage
        v-bind="data"
        class="size-full"
        :class="{
          'object-cover': props.mediaFit === 'cover',
          'object-contain': props.mediaFit === 'contain',
        }"
        alt=""
      />
    </template>
  </MediaRoot>
</template>
